import { ColorContext } from 'App';
import WidgetSelector from 'features/dashboard/features/WidgetSelector';
import DashboardPreview from 'pages/Dashboard/DashboardPreview';
import { useContext, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import Popup from 'reactjs-popup';

const DashboardEditTab = ({
  formData,
  setFormData,
  checks,
  widgetData,
  permList,
  onNext,
  compactPreview = false,
}) => {
  const [widgetSelectorOpen, _setWidgetSelectorOpen] = useState(false);
  const [previewLayout, setPreviewLayout] = useState(formData.layout || []);
  const previewLayoutRef = useRef(previewLayout);

  const setWidgetSelectorOpen = (open) => {
    _setWidgetSelectorOpen(open);
  };

  const theme = useContext(ColorContext);

  const normalizePermName = (permName) =>
    permName.replace(/\s/g, '').replace(/\//g, '-').replace(/:/g, '_');

  const hasConnectionPermission = (regex) =>
    (permList || []).some(
      (perm, index) => regex.test(normalizePermName(perm.name)) && formData.permissions[index],
    );

  const checkWidgetPermission = (permName) => {
    switch (permName) {
      case 'ViewConnections':
        return hasConnectionPermission(/^View[A-Za-z]+Connections$/);
      case 'Create-EditConnections':
        return hasConnectionPermission(/^Create-Edit[A-Za-z]+Connections$/);
      case 'DeleteConnections':
        return hasConnectionPermission(/^Delete[A-Za-z]+Connections$/);
      case 'ImportConnections':
        return hasConnectionPermission(/^Import[A-Za-z]+Connections$/);
      default: {
        const index = (permList || []).findIndex(
          (perm) => normalizePermName(perm.name) === permName,
        );
        return index >= 0 ? !!formData.permissions[index] : false;
      }
    }
  };

  useEffect(() => {
    setPreviewLayout(formData.layout || []);
  }, [formData.layout]);

  useEffect(() => {
    previewLayoutRef.current = previewLayout;
  }, [previewLayout]);

  useEffect(() => {
    return () => {
      setFormData((prev) => {
        if (prev.layout === previewLayoutRef.current) return prev;
        return { ...prev, layout: previewLayoutRef.current };
      });
    };
  }, [setFormData]);

  const persistLayout = (nextLayout) => {
    setFormData((prev) => {
      if (prev.layout === nextLayout) return prev;
      return { ...prev, layout: nextLayout };
    });
  };

  const addWidgets = (widgets) => {
    if (!Array.isArray(widgets) || !widgets.length) return;
    setPreviewLayout((prev) => [...prev, ...widgets]);
  };

  const removeWidget = (index) => {
    setPreviewLayout((prev) => prev.filter((_, i) => i !== index));
  };

  const onListChange = (newList) => {
    setPreviewLayout([...newList]);
  };

  const onSaveWidgetView = () => {
    persistLayout(previewLayout);
    if (typeof onNext === 'function') {
      onNext();
    }
  };

  return (
    <div className="d-flex flex-column w-100 h-100 user-select-none" style={{ minHeight: 0 }}>
      <div className="d-flex flex-row flex-grow-1 min-h-0 overflow-hidden border border-1 border-white bg-background position-relative">
        <DashboardPreview
          definedWidgetData={widgetData}
          list={previewLayout}
          setList={onListChange}
          onRemoveWidget={removeWidget}
          openWidgetSelector={() => setWidgetSelectorOpen(true)}
          compactPreview={compactPreview}
        />
        <Button
          className="position-absolute bottom-0 end-0 m-3 p-1 px-2 border-3"
          style={{ backgroundColor: theme.primaryHighlight, zIndex: 3 }}
          onClick={onSaveWidgetView}
        >
          Save Widget View
        </Button>
      </div>
      {widgetData && (
        <Popup
          open={widgetSelectorOpen}
          closeOnDocumentClick={false}
          onClose={() => setWidgetSelectorOpen(false)}
        >
          <WidgetSelector
            closeSelector={() => {
              setWidgetSelectorOpen(false);
            }}
            show={widgetSelectorOpen}
            handleShow={() => setWidgetSelectorOpen(false)}
            widgetData={widgetData}
            userList={Object.keys(widgetData)
              .filter((widgetId) =>
                (widgetData[widgetId].permissions || []).every((permName) =>
                  checkWidgetPermission(permName),
                ),
              )
              .map((widgetId) => {
                return widgetData[widgetId];
              })}
            onAddWidgets={addWidgets}
          />
        </Popup>
      )}
    </div>
  );
};

export default DashboardEditTab;
